import i18n from '@/i18n';

export type ClipStatusLike = {
  clipping_status: string;
  preparing_status?: string | null;
  rendering_status?: string | null;
} | null | undefined;

const t = (key: string) => i18n.t(key);

export const getClippingStatusText = (status: string): string => {
  const map: Record<string, () => string> = {
    queued: () => t('clipStatus.queuedProcessing'),
    processing: () => t('clipStatus.exportingVideo'),
    success: () => t('clipStatus.videoReady'),
    failure: () => t('clipStatus.processingError'),
    expired: () => t('clipStatus.expired'),
  };
  return map[status]?.() ?? status;
};

export const getPreparingStatusText = (status: string): string => {
  const map: Record<string, () => string> = {
    'pre-queued': () => t('clipStatus.waitingSceneDetection'),
    queued: () => t('clipStatus.waitingSceneDetection'),
    processing: () => t('clipStatus.detectingScenes'),
    success: () => t('clipStatus.readyForEditing'),
    failure: () => t('clipStatus.processingError'),
    expired: () => t('clipStatus.expired'),
  };
  return map[status]?.() ?? status;
};

export const getRenderingStatusText = (status: string): string => {
  const map: Record<string, () => string> = {
    'pre-queued': () => t('clipStatus.waitingGeneration'),
    queued: () => t('clipStatus.waitingGeneration'),
    processing: () => t('clipStatus.generatingFinalVideo'),
    success: () => t('clipStatus.videoReady'),
    failure: () => t('clipStatus.processingError'),
    expired: () => t('clipStatus.expired'),
  };
  return map[status]?.() ?? status;
};

// Keep backward-compatible map exports for any direct access
export const CLIPPING_STATUS_TEXT_MAP = new Proxy({} as Record<string, string>, {
  get: (_, key: string) => getClippingStatusText(key),
});

export const PREPARING_STATUS_TEXT_MAP = new Proxy({} as Record<string, string>, {
  get: (_, key: string) => getPreparingStatusText(key),
});

export const RENDERING_STATUS_TEXT_MAP = new Proxy({} as Record<string, string>, {
  get: (_, key: string) => getRenderingStatusText(key),
});

export function getClipStatusText(clip: ClipStatusLike): string {
  if (!clip) {
    return t('clipStatus.loading');
  }

  const clipping = clip.clipping_status;
  const preparing = clip.preparing_status ?? undefined;
  const rendering = clip.rendering_status ?? undefined;

  // Check for expired status first
  if (clipping === 'expired' || preparing === 'expired' || rendering === 'expired') {
    return t('clipStatus.expired');
  }

  // Finished rendering takes priority regardless of other statuses (e.g. library clips)
  if (rendering === 'success') {
    return getRenderingStatusText('success');
  }

  if (clipping === 'success' && preparing === 'pre-queued') {
    return getPreparingStatusText('pre-queued') || t('clipStatus.preparingVideo');
  }

  if (preparing === 'success' && rendering === 'pre-queued') {
    return getRenderingStatusText('pre-queued') || t('clipStatus.generatingFinalVideo');
  }

  if (preparing === 'skipped' || !preparing) {
    // If rendering has a meaningful status, use it instead of falling back to clipping
    if (rendering && rendering !== 'skipped') {
      return getRenderingStatusText(rendering) || t('clipStatus.generatingVideo');
    }
    return getClippingStatusText(clipping) || t('clipStatus.cuttingVideo');
  }

  if (preparing === 'pre-queued') {
    return getClippingStatusText(clipping) || t('clipStatus.cuttingVideo');
  }

  if (!rendering || rendering === 'skipped') {
    return getPreparingStatusText(preparing) || t('clipStatus.preparingVideo');
  }

  if (clipping !== 'success' && clipping !== 'failure') {
    return getClippingStatusText(clipping) || t('clipStatus.cuttingVideo');
  }

  if (preparing !== 'success' && preparing !== 'failure') {
    return getPreparingStatusText(preparing) || t('clipStatus.preparingVideo');
  }

  return getRenderingStatusText(rendering) || t('clipStatus.generatingVideo');
}
